import React, { FC, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  CreateCompanyResearchResponse,
  CompanyResearchErrorResponse,
  CompanyResearchMode,
} from '@dreamai/types';
import api from '../../../api';

import { ReactComponent as DeepResearchIcon } from '../../../icons/deep-research.svg';
import { ReactComponent as BroadResearchIcon } from '../../../icons/broad-research.svg';
import { ReactComponent as CampaignIcon } from '../../../icons/campaign.svg';
import { ReactComponent as ChevronRightIcon } from '../../../icons/chevron-right.svg';
import './create-company-research-modal.scss';

interface CreateCompanyResearchModalProps {
  show: boolean;
  onClose: () => void;
  agentName?: string;
  agentId: string;
  onOpenCampaignModal?: () => void;
}

const CreateCompanyResearchModal: FC<CreateCompanyResearchModalProps> = ({
  show,
  onClose,
  agentName = 'Sales Assistant',
  agentId,
  onOpenCampaignModal,
}) => {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreateResearch = async (mode: CompanyResearchMode) => {
    setLoading(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();
      const response = await api.post<
        CreateCompanyResearchResponse | CompanyResearchErrorResponse
      >(
        '/company-research',
        { mode, agentId },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success) {
        const { threadId, agentId } =
          response.data as CreateCompanyResearchResponse;
        onClose();
        navigate(`/conversations?agent_id=${agentId}&thread_id=${threadId}`);
      } else {
        setError((response.data as CompanyResearchErrorResponse).error);
      }
    } catch (err) {
      console.error('Error creating company research:', err);
      setError('Failed to create company research. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCampaign = () => {
    onClose();
    onOpenCampaignModal?.();
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size='lg'
      dialogClassName='create-company-research-modal'
    >
      <Modal.Header>
        <Modal.Title>Company research</Modal.Title>
        <button className='cancel-btn' onClick={onClose}>
          Cancel
        </button>
      </Modal.Header>

      <Modal.Body>
        <div className='research-wrapper'>
          <p className='create-research-subtitle'>
            {agentName} will research companies for you.
          </p>

          {error && <p className='create-research-error'>{error}</p>}

          <div className='create-research-options'>
            <div className='research-option single'>
              <div className='option-icon'>
                <DeepResearchIcon />
              </div>
              <h2 className='option-title'>Deep research one company</h2>
              <p className='option-description'>
                Get detailed information on a single company. Find out about the
                business, their products, team, and more.
              </p>
              <div className='option-button-wrapper'>
                <button
                  className='option-btn'
                  onClick={() => handleCreateResearch('single')}
                  disabled={loading}
                >
                  {loading ? 'Creating...' : 'Start deep research'}
                </button>
              </div>
            </div>

            <div className='research-option multiple'>
              <div className='option-icon'>
                <BroadResearchIcon />
              </div>
              <h2 className='option-title'>Broad research on several</h2>
              <p className='option-description'>
                Create a spreadsheet of research on multiple companies for
                comparison or targeting.
              </p>
              <div className='option-button-wrapper'>
                <button
                  className='option-btn'
                  onClick={() => handleCreateResearch('multiple')}
                  disabled={loading}
                >
                  {loading ? 'Creating...' : 'Start broad research'}
                </button>
              </div>
            </div>
          </div>

          <div className='campaign-divider'>
            <span>OR</span>
          </div>

          <button className='campaign-link' onClick={handleOpenCampaign}>
            <div className='campaign-link-icon'>
              <CampaignIcon />
            </div>
            <div className='campaign-link-content'>
              <span className='campaign-link-title'>
                Need to contact companies?
              </span>
              <span className='campaign-link-description'>
                Use a Campaign to find companies, then contact them with custom
                messaging.
              </span>
            </div>
            <ChevronRightIcon className='campaign-link-chevron' />
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default CreateCompanyResearchModal;
